import { api } from './api';
import type {
  CreateFormRequest,
  ExportFormResponsesResult,
  Form,
  FormDefaultSettings,
  FormField,
  FormFieldOption,
  FormFieldType,
  FormResponse,
  FormResponseDraft,
  FormSection,
  FormSummary,
  SaveFormResponseDraftRequest,
  SubmitFormResponseRequest,
  UpdateFormDefaultSettingsRequest,
  UpdateFormRequest,
} from '../types/form';
import { resolveFormTheme } from '../types/form';

export interface FormMediaUploadResult {
  objectKey: string;
  url: string;
}

function createId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function createDefaultOption(order: number, label?: string): FormFieldOption {
  return {
    id: createId(),
    label: label ?? `Opção ${order + 1}`,
    order,
  };
}

function createEmptyField(
  sectionId: string,
  order: number,
  isRequired = false
): FormField {
  return {
    id: createId(),
    sectionId,
    title: 'Pergunta sem título',
    type: 'MultipleChoice',
    isRequired,
    order,
    goToSectionBasedOnAnswer: false,
    options: [createDefaultOption(0), createDefaultOption(1)],
  };
}

function createEmptyMediaField(
  type: 'Image' | 'Video',
  sectionId: string,
  order: number
): FormField {
  return {
    id: createId(),
    sectionId,
    title: type === 'Image' ? 'Imagem' : 'Vídeo',
    type,
    isRequired: false,
    order,
    goToSectionBasedOnAnswer: false,
    options: [],
  };
}

function createEmptySection(order: number): FormSection {
  return {
    id: createId(),
    title: 'Seção sem título',
    order,
    afterSectionAction: 'ContinueToNext',
    afterSectionTargetId: null,
  };
}

function normalizeForm(form: Form): Form {
  return {
    ...form,
    description: form.description ?? undefined,
    createdBy: form.createdBy ?? undefined,
    purpose: form.purpose ?? 'General',
    collectEmail: form.collectEmail ?? 'NotCollect',
    limitOneResponsePerUser: form.limitOneResponsePerUser ?? false,
    showProgressBar: form.showProgressBar ?? true,
    confirmationMessage: form.confirmationMessage ?? undefined,
    showSubmitAnotherResponseLink: form.showSubmitAnotherResponseLink ?? false,
    allowResponseEditing: form.allowResponseEditing ?? false,
    responseEditingDeadlineDays: form.responseEditingDeadlineDays ?? null,
    allowDraftResponses: form.allowDraftResponses ?? true,
    sendResponseCopyToRespondent: form.sendResponseCopyToRespondent ?? false,
    fieldsRequiredByDefault: form.fieldsRequiredByDefault ?? false,
    theme: resolveFormTheme(form.theme),
    sections: (form.sections ?? []).map((section) => ({
      ...section,
      description: section.description ?? undefined,
      afterSectionAction: section.afterSectionAction ?? 'ContinueToNext',
      afterSectionTargetId: section.afterSectionTargetId ?? null,
    })),
    fields: (form.fields ?? []).map((field) => ({
      ...field,
      description: field.description ?? undefined,
      validationRegex: field.validationRegex ?? undefined,
      validationMessage: field.validationMessage ?? undefined,
      settingsJson: field.settingsJson ?? undefined,
      goToSectionBasedOnAnswer: field.goToSectionBasedOnAnswer ?? false,
      mediaUrl: field.mediaUrl ?? undefined,
      inlineImageUrl: field.inlineImageUrl ?? undefined,
      options: (field.options ?? []).map((option) => ({
        ...option,
        goToAction: option.goToAction ?? null,
        goToTargetSectionId: option.goToTargetSectionId ?? null,
      })),
    })),
  };
}

function normalizeResponse(response: FormResponse): FormResponse {
  return {
    ...response,
    respondentUserId: response.respondentUserId ?? undefined,
    respondentEmail: response.respondentEmail ?? undefined,
    confirmationMessage: response.confirmationMessage ?? undefined,
    showSubmitAnotherResponseLink: response.showSubmitAnotherResponseLink ?? false,
    allowResponseEditing: response.allowResponseEditing ?? false,
    answers: (response.answers ?? []).map((answer) => ({
      ...answer,
      valueText: answer.valueText ?? undefined,
      documentContentId: answer.documentContentId ?? undefined,
      documentId: answer.documentId ?? undefined,
      fileName: answer.fileName ?? undefined,
      contentType: answer.contentType ?? undefined,
      fileBase64: answer.fileBase64 ?? undefined,
    })),
  };
}

class FormService {
  async getAll(): Promise<FormSummary[]> {
    const response = await api.get<FormSummary[]>('/forms');
    return response.data ?? [];
  }

  async getById(id: string): Promise<Form> {
    const response = await api.get<Form>(`/forms/${id}`);
    return normalizeForm(response.data);
  }

  async create(request: CreateFormRequest): Promise<Form> {
    const response = await api.post<Form>('/forms', request);
    return normalizeForm(response.data);
  }

  async update(id: string, request: UpdateFormRequest): Promise<Form> {
    const response = await api.put<Form>(`/forms/${id}`, request);
    return normalizeForm(response.data);
  }

  async delete(id: string, deleteLinkedDocuments = false): Promise<void> {
    await api.delete(`/forms/${id}`, {
      params: { deleteLinkedDocuments },
    });
  }

  async publish(id: string): Promise<Form> {
    const response = await api.post<Form>(`/forms/${id}/publish`);
    return normalizeForm(response.data);
  }

  async close(id: string): Promise<Form> {
    const response = await api.post<Form>(`/forms/${id}/close`);
    return normalizeForm(response.data);
  }

  async getResponses(formId: string): Promise<FormResponse[]> {
    const response = await api.get<FormResponse[]>(`/forms/${formId}/responses`);
    return (response.data ?? []).map(normalizeResponse);
  }

  async exportToExcel(formId: string): Promise<ExportFormResponsesResult> {
    const response = await api.post<ExportFormResponsesResult>(
      `/forms/${formId}/export`
    );
    return response.data;
  }

  async getDefaultSettings(): Promise<FormDefaultSettings> {
    const response = await api.get<FormDefaultSettings>('/forms/default-settings');
    return response.data;
  }

  async updateDefaultSettings(
    request: UpdateFormDefaultSettingsRequest
  ): Promise<FormDefaultSettings> {
    const response = await api.put<FormDefaultSettings>(
      '/forms/default-settings',
      request
    );
    return response.data;
  }

  async uploadThemeHeader(formId: string, file: File): Promise<Form> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<Form>(
      `/forms/${formId}/theme/header`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return normalizeForm(response.data);
  }

  async uploadFormMediaImage(
    formId: string,
    file: File
  ): Promise<FormMediaUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<FormMediaUploadResult>(
      `/forms/${formId}/media/image`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  }

  async deleteThemeHeader(formId: string): Promise<Form> {
    const response = await api.delete<Form>(`/forms/${formId}/theme/header`);
    return normalizeForm(response.data);
  }

  async getPublicByPublicId(publicId: string): Promise<Form> {
    const response = await api.get<Form>(`/public/forms/${publicId}`);
    return normalizeForm(response.data);
  }

  async getMyResponse(publicId: string): Promise<FormResponse | null> {
    try {
      const response = await api.get<FormResponse>(
        `/public/forms/${publicId}/my-response`
      );
      return normalizeResponse(response.data);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return null;
      }
      throw error;
    }
  }

  async submitResponse(
    publicId: string,
    payload: SubmitFormResponseRequest
  ): Promise<FormResponse> {
    const response = await api.post<FormResponse>(
      `/public/forms/${publicId}/responses`,
      payload
    );
    return normalizeResponse(response.data);
  }

  async updateResponse(
    publicId: string,
    responseId: string,
    payload: SubmitFormResponseRequest
  ): Promise<FormResponse> {
    const response = await api.put<FormResponse>(
      `/public/forms/${publicId}/responses/${responseId}`,
      payload
    );
    return normalizeResponse(response.data);
  }

  async getDraft(
    publicId: string,
    sessionId?: string
  ): Promise<FormResponseDraft | null> {
    try {
      const response = await api.get<FormResponseDraft>(
        `/public/forms/${publicId}/draft`,
        { params: sessionId ? { sessionId } : undefined }
      );
      return response.data;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return null;
      }
      throw error;
    }
  }

  async saveDraft(
    publicId: string,
    payload: SaveFormResponseDraftRequest
  ): Promise<FormResponseDraft> {
    const response = await api.put<FormResponseDraft>(
      `/public/forms/${publicId}/draft`,
      payload
    );
    return response.data;
  }

  createEmptyField(
    sectionId: string,
    order: number,
    isRequired = false
  ): FormField {
    return createEmptyField(sectionId, order, isRequired);
  }

  createEmptyMediaField(
    type: Extract<FormFieldType, 'Image' | 'Video'>,
    sectionId: string,
    order: number
  ): FormField {
    return createEmptyMediaField(type, sectionId, order);
  }

  createEmptySection(order: number): FormSection {
    return createEmptySection(order);
  }

  createDefaultOption(order: number, label?: string): FormFieldOption {
    return createDefaultOption(order, label);
  }

  createId(): string {
    return createId();
  }
}

export const formService = new FormService();
